
// Deployment trigger: 2026-04-24
import React from 'react';
import { Edit2, ToggleLeft, ToggleRight, Trash2, PieChart, Ticket, Trophy, Info, AlertCircle } from 'lucide-react';
import { Campaign, GameType } from '../types';

interface CampaignListProps {
  campaigns: Campaign[];
  onEdit: (c: Campaign) => void;
  onDrawWinners: (c: Campaign) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdateEndDate?: (id: string, endDate: string) => void;
  embedDisplayMode?: 'random' | 'all';
}

const CampaignList: React.FC<CampaignListProps> = ({ campaigns, onEdit, onDrawWinners, onToggle, onDelete, onUpdateEndDate, embedDisplayMode }) => {
  const isExpired = (endDate?: string) => {
    if (!endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = endDate.split('-').map(Number);
    const end = new Date(year, month - 1, day);
    return today > end;
  };

  const activeCount = campaigns.filter(c => c.active && !isExpired(c.endDate)).length;
  // Default is "show all" (matches the project script). The banner below adapts
  // to whichever mode the project uses so the guidance is always accurate.
  const mode = embedDisplayMode ?? 'all';

  return (
    <div className="space-y-4">
      {mode === 'all' ? (
        campaigns.length > 1 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <span className="font-bold">“Show All Available Games” is on for this project.</span> Every active game appears as its own launcher on your website.{activeCount > 1 ? ` You currently have ${activeCount} active games, so visitors will see ${activeCount} launchers at once.` : ''} For the cleanest visitor experience, we recommend keeping a single game active at a time — multiple simultaneous games can clutter the page and distract from conversions. To rotate one random game per visit instead, switch this project's Embed Display to “Randomly Pick 1” in Settings.
            </div>
          </div>
        )
      ) : (
        activeCount > 1 && (
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
            <div className="text-sm text-indigo-900">
              <span className="font-bold">A/B Testing is Active:</span> You have {activeCount} active campaigns. Customers will randomly see one of these active campaigns when they visit your website. Showing multiple floating buttons or a confusing carousel of games would overwhelm the user. Showing one random active game keeps the user experience clean and focused while letting you test which game converts best!
            </div>
          </div>
        )
      )}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left border-collapse min-w-[700px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Campaign</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Type</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Status</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Expiration Date</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest text-center">Leads</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest text-center">CVR</th>
              <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {campaigns.map((c) => {
              const expired = isExpired(c.endDate);
              return (
              <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                    {c.name}
                    {c.type === GameType.RAFFLE && <Ticket className="w-3.5 h-3.5 text-indigo-500" />}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Created {c.createdAt}</div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase ${c.type === GameType.RAFFLE ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'}`}>
                    {c.type.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button 
                    onClick={() => onToggle(c.id)}
                    className={`flex items-center gap-2 transition-all ${c.active ? (expired ? 'text-amber-500' : 'text-emerald-600') : 'text-slate-300'}`}
                  >
                    {c.active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                    <span className="text-[10px] font-bold uppercase">{c.active ? (expired ? 'Expired' : 'Active') : 'Paused'}</span>
                  </button>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="date"
                    value={c.endDate || ''}
                    onChange={(e) => onUpdateEndDate && onUpdateEndDate(c.id, e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                  />
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="text-sm font-bold text-slate-700">{c.leadsCount}</div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="text-sm font-bold text-indigo-600">{c.conversionRate}%</div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {c.type === GameType.RAFFLE && (
                      <button 
                        onClick={() => onDrawWinners(c)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest transition-all mr-2 shadow-lg shadow-amber-100"
                        title="Pick Winners"
                      >
                        <Trophy className="w-3.5 h-3.5" />
                        Draw
                      </button>
                    )}
                    <button 
                      onClick={() => onEdit(c)}
                      className="p-2 hover:bg-indigo-50 rounded-lg text-slate-400 hover:text-indigo-600 transition-all"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => onDelete(c.id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>
      {campaigns.length === 0 && (
        <div className="px-6 py-20 text-center">
          <PieChart className="w-8 h-8 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No campaigns found.</p>
        </div>
      )}
      </div>
    </div>
  );
};

export default CampaignList;
